import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Pagination,
  Skeleton,
  Table,
  Text,
  createStyles,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronRight,
  IconExternalLink,
  IconHeartbeat,
} from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { getSkypointMissingCarePlans } from "../../../services/skypointAlerts";
import { enterProviderView } from "../../../services/skypointProvider";

const LIMIT = 25;
const TOTAL_COLS = 4;

const useStyles = createStyles((theme) => ({
  container: { width: "100%" },
  stickyBanner: { position: "sticky", top: 0, zIndex: 100 },
  content: {
    padding: "24px 28px",
    [theme.fn.smallerThan("sm")]: { padding: "16px 12px" },
  },
  backRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#667085",
    cursor: "pointer",
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 12,
    "&:hover": { color: "#101828" },
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 20,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: "#FEF3F2",
  },
  title: {
    fontFamily: "Inter",
    fontSize: 22,
    fontWeight: 700,
    color: "#101828",
    lineHeight: 1.2,
  },
  subtitle: {
    fontFamily: "Inter",
    fontSize: 13,
    color: "#667085",
    marginTop: 2,
  },
  tableWrap: {
    background: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: 12,
    overflow: "hidden",
  },
  table: {
    "& thead tr th": {
      backgroundColor: "#F9FAFB",
      fontFamily: "Inter",
      fontSize: 12,
      fontWeight: 600,
      color: "#667085",
      textTransform: "uppercase",
      letterSpacing: 0.4,
      borderBottom: "1px solid #EAECF0",
    },
    "& tbody tr td": {
      fontFamily: "Inter",
      fontSize: 13,
      color: "#101828",
      borderBottom: "1px solid #F2F4F7",
      verticalAlign: "middle",
    },
    "& tbody tr:last-of-type td": { borderBottom: "none" },
  },
  groupRow: {
    backgroundColor: "#F9FAFB",
    cursor: "pointer",
    userSelect: "none",
    "& td": {
      borderBottom: "1px solid #EAECF0 !important",
      padding: "10px 16px !important",
    },
    "&:hover td": {
      backgroundColor: "#F3F4F6",
    },
  },
  childRow: {
    "& td:first-of-type": {
      paddingLeft: "40px !important",
    },
  },
  emptyState: {
    padding: "48px 20px",
    textAlign: "center",
    color: "#667085",
    fontFamily: "Inter",
    fontSize: 14,
  },
  paginationRow: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "12px 16px",
    borderTop: "1px solid #EAECF0",
  },
}));

const MissingCarePlans = ({ isSidebarOpen }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const data = useSelector((state) => state?.skypointAlerts?.missingCarePlans) || {};
  const items = useMemo(() => data.items || [], [data.items]);
  const total = data.total || 0;
  const loading = !!data.loading;

  const [page, setPage] = useState(1);
  const [expandedProviders, setExpandedProviders] = useState(new Set());

  useEffect(() => {
    getSkypointMissingCarePlans({ page, limit: LIMIT });
  }, [page]);

  useEffect(() => {
    setExpandedProviders(new Set());
  }, [page]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / LIMIT)), [total]);

  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.providerId || item.providerName;
      if (!map.has(key)) {
        map.set(key, {
          key,
          providerId: item.providerId,
          providerName: item.providerName,
          items: [],
        });
      }
      map.get(key).items.push(item);
    });
    return Array.from(map.values());
  }, [items]);

  const toggleProvider = (key) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleViewProvider = async (providerId, providerName) => {
    const ok = await enterProviderView(providerId, providerName);
    if (ok) navigate("/home/provider-view");
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.stickyBanner}>
        <AppNavbar title="Home" profile={profile} isSidebarOpen={isSidebarOpen} />
      </Box>

      <Box className={classes.content}>
        <Box
          className={classes.backRow}
          onClick={() => navigate("/home/dashboard-skypoint")}
        >
          <IconArrowLeft size={16} />
          <span>Back to dashboard</span>
        </Box>

        <Box className={classes.headerRow}>
          <Box className={classes.headerIcon}>
            <IconHeartbeat size={22} color="#F04438" />
          </Box>
          <Box>
            <Text className={classes.title}>Missing Care Plans</Text>
            <Text className={classes.subtitle}>
              Active clients without a recorded support / care plan
              {total > 0 && ` · ${total} ${total === 1 ? "client" : "clients"}`}
            </Text>
          </Box>
        </Box>

        <Box className={classes.tableWrap}>
          <Table className={classes.table} verticalSpacing="md">
            <thead>
              <tr>
                <th>Client Name</th>
                <th style={{ width: 140 }}>Admitted</th>
                <th style={{ width: 170 }}>Days Without Plan</th>
                <th style={{ width: 160 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={TOTAL_COLS}>
                      <Skeleton height={32} radius={6} />
                    </td>
                  </tr>
                ))
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={TOTAL_COLS} className={classes.emptyState}>
                    Every active client has a care plan recorded.
                  </td>
                </tr>
              ) : (
                groups.map((group) => {
                  const isExpanded = expandedProviders.has(group.key);
                  return (
                    <React.Fragment key={group.key}>
                      <tr
                        className={classes.groupRow}
                        onClick={() => toggleProvider(group.key)}
                      >
                        <td colSpan={TOTAL_COLS}>
                          <Flex align="center" gap={10}>
                            {isExpanded ? (
                              <IconChevronDown size={16} color="#667085" />
                            ) : (
                              <IconChevronRight size={16} color="#667085" />
                            )}
                            <Text weight={600} size="sm" color="#101828">
                              {group.providerName || "Unknown Provider"}
                            </Text>
                            <Badge size="sm" variant="filled" color="red" radius="xl">
                              {group.items.length}{" "}
                              {group.items.length === 1 ? "client" : "clients"}
                            </Badge>
                          </Flex>
                        </td>
                      </tr>

                      {isExpanded &&
                        group.items.map((item) => (
                          <tr
                            key={item.relatedId || item._id}
                            className={classes.childRow}
                          >
                            <td>
                              <Text size="sm">{item.clientName || "Unknown client"}</Text>
                            </td>
                            <td>
                              <Text size="xs" color="dimmed">
                                {item.admittedDate
                                  ? new Date(item.admittedDate).toLocaleDateString()
                                  : "—"}
                              </Text>
                            </td>
                            <td>
                              <Badge
                                color={
                                  item.daysWithoutPlan >= 30
                                    ? "red"
                                    : item.daysWithoutPlan >= 14
                                    ? "orange"
                                    : "yellow"
                                }
                                variant="light"
                                size="sm"
                              >
                                {item.daysWithoutPlan}{" "}
                                {item.daysWithoutPlan === 1 ? "day" : "days"}
                              </Badge>
                            </td>
                            <td>
                              <Button
                                size="xs"
                                variant="subtle"
                                leftIcon={<IconExternalLink size={14} />}
                                onClick={() =>
                                  handleViewProvider(item.providerId, item.providerName)
                                }
                              >
                                Ogiso Core
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>

          {totalPages > 1 && (
            <Box className={classes.paginationRow}>
              <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MissingCarePlans;
